import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { CreateTagBody, Tag } from '@lira/shared';
import { apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { Sheet } from '@/components/ui';

interface TagFormModalProps {
  open: boolean;
  onClose: () => void;
  tag?: Tag;
}

/** Formulário minimalista de tag (apenas nome). Cria ou edita conforme `tag`. */
export function TagFormModal({ open, onClose, tag }: TagFormModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName(tag?.name ?? '');
  }, [open, tag]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: CreateTagBody = { name: name.trim() };
      return tag
        ? apiPatch<{ tag: Tag }>(`/tags/${tag.id}`, body)
        : apiPost<{ tag: Tag }>('/tags', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
      onClose();
    },
  });

  const canSave = name.trim().length > 0 && !mutation.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate();
  };

  return (
    <Sheet open={open} onClose={onClose} title={tag ? 'Editar tag' : 'Nova tag'} maxWidth={400}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="tag-name" className="mb-1 block text-[13px] text-muted">
            Nome
          </label>
          <input
            id="tag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Ex.: Viagem, Trabalho, Reembolsável"
            autoComplete="off"
            autoFocus
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
          />
        </div>

        {mutation.isError && <p className="text-sm text-expense">{errorMessage(mutation.error)}</p>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
